// Persistent, user-created measurement entities — the "Workspace content"
// group in the Scene Manager (src/map/sceneTree.ts) and the map layer that
// renders them both read this. Each finished Point/Line/Zone/Circle drawn
// with the measure tools becomes one of these, same "named entity with its
// own detail panel" pattern already used for Field Photos
// (src/map/photoLayer.ts), not an ephemeral scratch shape.
import Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import Polygon, { fromCircle } from 'ol/geom/Polygon';
import CircleGeom from 'ol/geom/Circle';
import GeoJSONFormat from 'ol/format/GeoJSON';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style';
import type { ProjectionLike } from 'ol/proj';
import { toLonLat } from 'ol/proj';
import { getArea, getLength } from 'ol/sphere';
import { formatArea, formatLength } from './measurementFormat';
import type { GeoJsonGeometry } from '../api/measurements';

export type MeasurementType = 'point' | 'line' | 'zone' | 'circle';

export const MEASUREMENT_TYPE_LABEL: Record<MeasurementType, string> = {
  point: 'Point',
  line: 'Line',
  zone: 'Zone',
  circle: 'Circle',
};

export interface MeasurementEntity {
  id: string;
  type: MeasurementType;
  name: string;
  feature: Feature<Geometry>;
  createdAt: string; // ISO timestamp
  createdBy: string; // placeholder user, e.g. "HA" — see TopBar/PhotoDetailPanel's own convention
  description: string;
  visible: boolean;
  /** Hex color, e.g. "#2f7d4f". Undefined = the default accent (first
   * PRESET_COLOR below). Set via the detail panel's "Set colour" menu item. */
  color?: string;
  /** The user folder (platform_db workspace_folders) it's filed in; null/undefined = no folder. */
  folderId?: string | null;
}

const ACCENT = '#2f7d4f';

/** "Set colour" swatch choices — a small fixed palette, not a full picker,
 * per the plan's scoping ("5-8 preset colors is fine"). First entry is the
 * default/original accent so "no color set yet" and "explicitly set to
 * green" look identical. */
export const PRESET_COLORS = ['#2f7d4f', '#3b6fd6', '#d94f63', '#bf8f2d', '#8b5cf6', '#14b8a6', '#6f6f6f'];

function withAlpha(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const n = parseInt(full, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function styleFor(type: MeasurementType | undefined, label: string, color: string | undefined): Style | Style[] {
  const c = color || ACCENT;
  const stroke = new Stroke({ color: c, width: 2 });
  const fill = new Fill({ color: withAlpha(c, 0.18) });
  const text = new Text({
    text: label,
    font: '600 11px "Work Sans", sans-serif',
    fill: new Fill({ color: '#fff' }),
    stroke: new Stroke({ color: 'rgba(0,0,0,0.6)', width: 3 }),
    offsetY: -12,
  });
  switch (type) {
    case 'point':
      return new Style({
        image: new CircleStyle({ radius: 7, fill: new Fill({ color: c }), stroke: new Stroke({ color: '#fff', width: 2 }) }),
      });
    case 'line':
      return new Style({ stroke, text });
    case 'zone':
    case 'circle':
      return new Style({ stroke, fill, text });
    default:
      return new Style({ stroke, fill });
  }
}

/** Style function for the shared "Workspace content" vector layer. Reads
 * per-feature properties set by addMeasurement() (MapContext.tsx) rather
 * than needing a lookup back into React state — keeps OL's own render loop
 * independent of React's render cycle. A feature flagged `hidden` renders
 * with no style at all (OL's documented way to hide a feature without
 * removing it from the source). */
export function measurementStyleFunction(feature: { get: (key: string) => unknown }) {
  if (feature.get('hidden')) return undefined;
  const type = feature.get('measurementType') as MeasurementType | undefined;
  const name = (feature.get('measurementName') as string | undefined) ?? '';
  const color = feature.get('color') as string | undefined;
  return styleFor(type, name, color);
}

export function makeMeasurementsLayer() {
  const source = new VectorSource<Feature<Geometry>>();
  const layer = new VectorLayer({
    source,
    style: measurementStyleFunction,
    properties: { id: 'measurement:workspace', title: 'Workspace content', kind: 'measurement' },
  });
  return { source, layer };
}

function newId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Builds a fresh MeasurementEntity for a just-finished draw and stamps the
 * matching properties onto the OL feature itself (read by the style
 * function above). Feature is assumed already added to the measurements
 * source by the Draw interaction (it was given that source directly —
 * see MeasureToolbar.tsx). `id` is omitted for a brand-new local draw (a
 * client-generated placeholder, replaced with the API's real id once
 * MapContext's create call resolves — v0.6.5) and given explicitly when
 * hydrating a persisted measurement from the API on load. */
export function createMeasurementEntity(
  feature: Feature<Geometry>,
  type: MeasurementType,
  createdBy: string,
  id: string = newId(),
): MeasurementEntity {
  const name = MEASUREMENT_TYPE_LABEL[type];
  feature.setId(id);
  feature.set('measurementType', type);
  feature.set('measurementName', name);
  return {
    id,
    type,
    name,
    feature,
    createdAt: new Date().toISOString(),
    createdBy,
    description: '',
    visible: true,
  };
}

/** Clones a measurement's geometry (with a small on-screen offset so the
 * copy is visibly distinguishable, not stacked exactly on top of the
 * original) into a fresh, unregistered OL Feature — the caller
 * (MapContext.duplicateMeasurement) adds it to the measurements source and
 * wraps it as a new MeasurementEntity via createMeasurementEntity(). */
export function cloneMeasurementFeature(entity: MeasurementEntity): Feature<Geometry> | null {
  const geom = entity.feature.getGeometry();
  if (!geom) return null;
  const cloned = geom.clone();
  // 20m offset in the map's projected units (EPSG:3857 is ~meters) — small
  // and consistent regardless of current zoom, unlike a fixed pixel offset.
  cloned.translate(20, -20);
  return new Feature<Geometry>({ geometry: cloned });
}

export interface MeasurementProperty {
  label: string;
  value: string;
}

/** Properties shown in MeasurementDetailPanel, per the plan's spec:
 * Zone/Circle -> Surface Area + Horizontal Area (m²); Line -> Length (m);
 * Point -> X/Y/Z. Surface Area and Horizontal Area are equal here since
 * this is a 2D-only viewer with no terrain/elevation model yet (v0.5.1
 * adds 3D) — both are computed and shown anyway to match the reference
 * panel's shape; they'll diverge once real elevation data exists. */
export function measurementProperties(entity: MeasurementEntity, projection: ProjectionLike): MeasurementProperty[] {
  const geom = entity.feature.getGeometry();
  if (!geom) return [];
  if (geom instanceof Point) {
    const [lon, lat] = toLonLat(geom.getCoordinates());
    return [
      { label: 'X (lon)', value: lon.toFixed(6) },
      { label: 'Y (lat)', value: lat.toFixed(6) },
      { label: 'Z', value: '—' },
    ];
  }
  if (geom instanceof LineString) {
    return [{ label: 'Length', value: formatLength(getLength(geom, { projection })) }];
  }
  if (geom instanceof Polygon || geom instanceof CircleGeom) {
    const area = formatArea(getArea(geom, { projection }));
    return [
      { label: 'Surface Area', value: area },
      { label: 'Horizontal Area', value: area },
    ];
  }
  return [];
}

// ---- GeoJSON <-> OL geometry, for GET/POST/PATCH /api/v1/measurements (v0.6.5) ----
//
// No projection option is passed to either reader/writer below -- both the OL feature's own
// coordinates and the API's stored geometry travel in the SAME projection at this boundary
// (EPSG:3857, the map's view projection), so this is an identity pass-through, not a transform.
// The API reprojects to EPSG:32638 for storage server-side (ARCHITECTURE.md Sec 5); this module
// never touches that CRS at all.
const geoJsonFormat = new GeoJSONFormat();

/** GeoJSON has no "Circle" type (neither does PostGIS) -- a Circle is approximated as a 64-sided
 * polygon before serializing, same as most PostGIS-backed editors do. `measurementType` metadata
 * (stored server-side, not in the geometry) is what lets the UI still render/label it as a
 * circle on the way back in. */
export function measurementGeometryToGeoJson(feature: Feature<Geometry>): GeoJsonGeometry {
  const geom = feature.getGeometry();
  if (!geom) throw new Error('measurementGeometryToGeoJson: feature has no geometry');
  const forWriting = geom instanceof CircleGeom ? fromCircle(geom, 64) : geom;
  return geoJsonFormat.writeGeometryObject(forWriting) as unknown as GeoJsonGeometry;
}

/** The inverse of measurementGeometryToGeoJson -- builds a fresh, unregistered OL Feature from a
 * GeoJSON geometry object as returned by the API. A circle round-trips as the same many-sided
 * polygon it was serialized as (never reconstructed back into an ol/geom/Circle) -- visually
 * indistinguishable, and area/length calculations already treat Polygon and Circle identically
 * (measurementProperties() above). */
export function featureFromGeoJson(geometry: GeoJsonGeometry): Feature<Geometry> {
  const geom = geoJsonFormat.readGeometry(geometry as never) as Geometry;
  return new Feature<Geometry>({ geometry: geom });
}
